import { useState } from "react";
import { AlertTriangle, MapPin, ChevronDown } from "lucide-react";
import { RISK, ASSET_META } from "@/lib/constants";

const TIER_MEANING = [
  ["critical", "Critical", "Flooded or in a gully-overflow zone at the current water level"],
  ["high", "High", "Floods within the next 72 h of the real forecast (tide, pressure, rain)"],
  ["medium", "Medium", "Would have flooded in a recorded storm (Tomas, Ivan, Elsa, Beryl replays)"],
  ["low", "Low", "Not reached by any of the above"],
];

function CalibrationNote({ tiers, inundation }) {
  const total = Object.values(tiers).reduce((a, b) => a + b, 0) || 1;
  const pct = (t) => `${((100 * (tiers[t] || 0)) / total).toFixed(1)}%`;
  const wl = inundation?.water_level_m ?? 0;
  const datum = inundation?.datum_m;
  const active = datum != null && wl > datum;
  const [open, setOpen] = useState(false);
  return (
    <div data-testid="ledger-calibration-note" className="mt-3 rounded-md border border-border bg-slate-900/50 px-2.5 py-1.5 font-mono-t text-[10.5px] leading-relaxed text-slate-400">
      <button data-testid="ledger-calibration-toggle" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-2 text-left">
        <span className="text-slate-200 tracking-widest text-[10px] shrink-0">CALIBRATION</span>
        <span data-testid="ledger-calibration-state" className="truncate" style={{ color: active ? RISK.critical.color : RISK.low.color }}>
          {active ? "above high-tide line" : "below high-tide line"}
        </span>
        <ChevronDown className={`w-3 h-3 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <div data-testid="ledger-calibration-split" className="mt-1 space-y-0.5">
        {TIER_MEANING.map(([t, name, text]) => (
          <div key={t} data-testid={`ledger-calibration-tier-${t}`} className="flex gap-2">
            <span className="w-[86px] shrink-0 font-bold" style={{ color: RISK[t].color }}>{name} {pct(t)}</span>
            <span className="text-slate-400">{text}</span>
          </div>
        ))}
      </div>
      {open && (
        <div data-testid="ledger-calibration-details" className="mt-1.5 pt-1.5 border-t border-border space-y-1">
          <div>Still-water level {wl.toFixed(2)} m vs high-tide line (MHHW) {datum ?? "—"} m — from harmonic analysis of the Bridgetown tide gauge.</div>
          <div>Flooding = SRTM 37 m ground below the water and connected to the sea; land below Highest Astronomical Tide ({inundation?.foreshore_m ?? "—"} m) is foreshore reached by normal tides and is not counted.</div>
          <div>No weights or scores: every tier is a computed flood outcome. Freeboard = ground height − water level.</div>
        </div>
      )}
    </div>
  );
}

export default function ReadinessLedger({ ledger, assets, visibleTiers, toggleTier, onSelect, inundation }) {
  const tiers = ledger?.tiers || {};
  const RANK = { critical: 3, high: 2, medium: 1, low: 0 };
  const redZones = [...assets].sort((a, b) => (RANK[b.tier] - RANK[a.tier]) || ((a.freeboard_m ?? 99) - (b.freeboard_m ?? 99))).slice(0, 40);

  return (
    <div data-testid="readiness-ledger" className="glass rounded-lg flex flex-col h-full overflow-y-auto drawer-scroll">
      <div className="p-4 border-b border-border">
        <h2 className="font-heading font-semibold uppercase tracking-wide text-sm text-slate-100 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-risk-high" /> Readiness Ledger
        </h2>
        <p className="font-mono-t text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">
          Impact-based flood exposure
        </p>

        {/* tier filter chips */}
        <div className="grid grid-cols-4 gap-1.5 mt-3">
          {["critical", "high", "medium", "low"].map((t) => {
            const rc = RISK[t];
            const active = visibleTiers.has(t);
            return (
              <button
                key={t}
                data-testid={`filter-${t}`}
                onClick={() => toggleTier(t)}
                className={`flex flex-col items-center py-2 rounded-md border transition-all ${
                  active ? "bg-slate-900/80" : "opacity-35 bg-slate-900/40"
                }`}
                style={{ borderColor: active ? rc.color : "#1e293b" }}
              >
                <span className="font-heading font-bold text-base leading-none" style={{ color: rc.color }}>
                  {tiers[t] ?? 0}
                </span>
                <span className="font-mono-t text-[9px] uppercase tracking-wider text-slate-400 mt-1">
                  {rc.label}
                </span>
              </button>
            );
          })}
        </div>
        <CalibrationNote tiers={tiers} inundation={inundation} />
      </div>

      {/* by type */}
      {ledger?.by_type && (
        <div className="px-4 py-3 border-b border-border grid grid-cols-2 gap-2">
          {Object.entries(ledger.by_type).map(([type, d]) => (
            <div key={type} data-testid={`type-stat-${type}`} className="bg-slate-900/50 rounded-md px-2.5 py-1.5 border border-border">
              <div className="font-mono-t text-[9px] uppercase tracking-wider text-slate-500">
                {ASSET_META[type]?.label || type}
              </div>
              <div className="flex items-baseline justify-between">
                <span className="font-heading font-bold text-slate-100">{d.count}</span>
                <span className="font-mono-t text-[10px]" style={{ color: RISK[d.critical ? "critical" : d.exposed ? "medium" : "low"].color }}>
                  {d.critical ? `${d.critical} flooded` : `${d.exposed} exposed`}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* red zone list */}
      <div className="p-3 space-y-2 min-h-[320px]">
        {tiers.critical ? (
          <div data-testid="ledger-list-heading" className="font-mono-t text-[10px] uppercase tracking-widest text-risk-critical px-1 mb-1 flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-risk-critical animate-pulse" /> Priority Red Zones
          </div>
        ) : (
          <div data-testid="ledger-list-heading" className="px-1 mb-1">
            <div className="font-mono-t text-[10px] uppercase tracking-widest text-risk-low">No red zones under current conditions</div>
            <div className="font-mono-t text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">Most exposed assets</div>
          </div>
        )}
        {redZones.map((a, i) => {
          const rc = RISK[a.tier];
          return (
            <button
              key={a.id}
              data-testid={`ledger-asset-${a.id}`}
              onClick={() => onSelect(a)}
              className="w-full text-left glass rounded-md p-2.5 border border-border hover:border-cyan/50 transition-all duration-200 fade-up group"
              style={{ animationDelay: `${Math.min(i, 12) * 25}ms` }}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-heading font-semibold text-sm text-slate-100 truncate group-hover:text-cyan transition-colors">
                    {a.name}
                  </div>
                  <div className="font-mono-t text-[10px] text-slate-500 uppercase tracking-wide flex items-center gap-1 mt-0.5">
                    <MapPin className="w-2.5 h-2.5" /> {a.asset_type} · {a.elevation_m}m · {Math.round(a.coast_distance_m)}m coast
                  </div>
                </div>
                <div
                  className="shrink-0 font-mono-t font-bold text-sm px-2 py-1 rounded"
                  style={{ color: rc.color, background: `${rc.color}1a`, border: `1px solid ${rc.color}` }}
                >
                  {a.freeboard_m != null ? `${a.freeboard_m > 0 ? "+" : ""}${a.freeboard_m}m` : "—"}
                </div>
              </div>
              <div className="mt-1 font-mono-t text-[9px] text-slate-500">
                ground {a.elevation_m ?? "—"} m · {a.tier === "critical" && a.freeboard_m != null && a.freeboard_m < 0 ? `${Math.abs(a.freeboard_m)} m under water` : a.freeboard_m != null && a.freeboard_m < 0 ? "below water level but not connected to the sea" : "freeboard above water"}
              </div>
            </button>
          );
        })}
        {redZones.length === 0 && (
          <div className="text-center text-slate-600 font-mono-t text-xs py-8">No assets in view.</div>
        )}
      </div>
    </div>
  );
}
